import { useEffect, useRef, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { useCart } from "../context/CartContext";

const OrderConfirmation = () => {
  const location = useLocation();
  const { orderId } = useParams();
  const navigate = useNavigate();
  const { clearCart } = useCart();
  const hasClearedCart = useRef(false);

  const [orderDetails, setOrderDetails] = useState(null);

  const BASE_URL = process.env.REACT_APP_API_BASE_URL || "http://localhost:5000";

  // 1. Set order details from location.state (after successful checkout)
  useEffect(() => {
    if (location.state?.orderDetails) {
      setOrderDetails(location.state.orderDetails);
      return;
    }

    // 2. If orderId is in the URL, fetch from backend
    if (orderId) {
      fetch(`${BASE_URL}/api/orders/${orderId}`, {
        credentials: "include",
      })
        .then((res) => {
          if (!res.ok) throw new Error("Order not found");
          return res.json();
        })
        .then((data) => {
          setOrderDetails(data);
        })
        .catch((err) => {
          console.error("Failed to fetch order:", err);
          navigate("/", { replace: true });
        });
    } else {
      // 3. Nothing to work with, redirect home
      navigate("/", { replace: true });
    }
  }, [location.state, orderId, BASE_URL, navigate]);

  // Optional: clear cart after success
  useEffect(() => {
    if (orderDetails && !hasClearedCart.current) {
      clearCart();
      hasClearedCart.current = true;
    }
  }, [orderDetails, clearCart]);

  const handleDownload = () => {
    window.print();
  };

  const orderNumber = orderDetails?.orderNumber || "N/A";
  if (!orderDetails || !orderNumber) return null;

  const {
    sameDayFee = 0,
    hamperDiscount = 0,
    promoCode = null,
    totalAmount = 0,
    paymentMethod = "",
    createdAt,
    hamperType = "",
    grouped = {},
    taxAmount = 0,
  } = orderDetails || {};

  const promoDiscount = promoCode?.discountValue || 0;

  const orderDate = createdAt
    ? new Date(createdAt).toLocaleDateString()
    : 'N/A';

  const hamperItems = grouped?.hamperItems || orderDetails.hamperItems || [];
  const extraFlowers = grouped?.selectedFlowers || orderDetails.selectedFlowers || [];
  const extraAccessories = grouped?.selectedAccessories || orderDetails.selectedAccessories || [];
  const extraVips = grouped?.selectedVips || orderDetails.selectedVips || [];

  return (
    <div className="max-w-3xl mx-auto p-6 bg-white shadow-md mt-10 rounded-xl print:p-0 print:shadow-none print:border-none print:mt-0">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-6 gap-4 print:flex-col print:items-start">
        <div className="flex items-start">
          <img
            src="/images/logos/Exudee-logo.svg"
            alt="Exudee Logo"
            className="h-10 sm:h-12 object-contain print:mb-4"
          />
        </div>
        <div className="text-left sm:text-right print:text-left">
          <h1 className="text-2xl font-bold text-green-600 mb-1">Order Confirmed!</h1>
          <p className="text-sm text-gray-600">Thank you for your order.</p>
        </div>
      </div>

      <div className="border-t pt-4 text-sm print:text-base">
        <div className="mb-2"><strong>Order Number:</strong> {orderNumber || "N/A"}</div>
        <div className="mb-2"><strong>Date:</strong> {orderDate || "N/A"}</div>
        <div className="mb-2"><strong>Payment Method:</strong> {paymentMethod.toUpperCase() || "N/A"}</div>

        <div className="border-t my-4"></div>

        {hamperItems.length > 0 && (
          <>
            <div className="mt-4">
              <h4 className="text-base font-semibold mb-2">
                Hamper Package: {hamperType}
              </h4>
              {hamperItems.map((item, idx) => (
                <div key={idx} className="flex justify-between mb-1">
                  <span>
                    {item.name} × {item.quantity || 1}
                  </span>
                  <span>
                    ${(Number(item.price) * (item.quantity || 1)).toFixed(2)}
                  </span>
                </div>
              ))}
            </div>

            {/*
            <div className="border-t my-4"></div>
            
            <div className="flex justify-between text-green-700 font-medium mb-1 mt-2">
              <span>Hamper Total × {hamperQuantity || 1}</span>
              <span>${(hamperSubtotal * (hamperQuantity || 1)).toFixed(2)}</span>
            </div>
            */}
          </>
        )}

        <div className="border-t my-4"></div>

        {extraFlowers.length > 0 && (
          <div>
            <h4 className="text-base font-semibold mt-4 mb-2">
              {hamperItems.length > 0 ? "Extra Flowers" : "Flowers"}
            </h4>

            {extraFlowers.map((f, idx) => (
              <div key={idx} className="flex justify-between mb-1">
                <span>{f.name} ×{f.quantity || 1}</span>
                <span>${(Number(f.price) * (f.quantity || 1)).toFixed(2)}</span>
              </div>
            ))}
          </div>
        )}

        {extraAccessories.length > 0 && (
          <div>
            <h4 className="text-base font-semibold mt-4 mb-2">
              {hamperItems.length > 0 ? "Extra Accessories" : "Accessories"}
            </h4>

            {extraAccessories.map((a, idx) => (
              <div key={idx} className="flex justify-between mb-1">
                <span>{a.name} ×{a.quantity || 1}</span>
                <span>${(Number(a.price) * (a.quantity || 1)).toFixed(2)}</span>
              </div>
            ))}
          </div>
        )}

        {extraVips.length > 0 && (
          <div>
            <h4 className="text-base font-semibold mt-4 mb-2">
              {hamperItems.length > 0 ? "Extra VIP Items" : "VIP Items"}
            </h4>
            {extraVips.map((v, idx) => (
              <div key={idx} className="flex justify-between mb-1">
                <span>{v.name} ×{v.quantity || 1}</span>
                <span>${(Number(v.price) * (v.quantity || 1)).toFixed(2)}</span>
              </div>
            ))}
          </div>
        )}

        <div className="border-t my-4"></div>

        <section className="mt-6 mb-6 bg-gray-50 p-4 rounded-lg border border-gray-200">
          {orderDetails.deliveryAddress && (
            <div className="mb-2">
              <h3 className="font-semibold">Delivery Address:</h3>
              <p className="text-sm text-gray-700">{orderDetails.deliveryAddress}</p>
            </div>
          )}
          {orderDetails.deliveryNote && (
            <div className="mb-2">
              <h3 className="font-semibold">Delivery Instruction:</h3>
              <p className="text-sm text-gray-700">{orderDetails.deliveryNote}</p>
            </div>
          )}
          {orderDetails.giftMessage && (
            <div>
              <h3 className="font-semibold">Special Gift Message:</h3>
              <p className="text-sm text-gray-700">{orderDetails.giftMessage}</p>
            </div>
          )}
        </section>

        <div className="border-t my-4"></div>

        {sameDayFee > 0 && (
          <div className="flex justify-between text-gray-700 mb-1 mt-2">
            <span>Same-Day Delivery Fee</span>
            <span>+${sameDayFee.toFixed(2)}</span>
          </div>
        )}

        {hamperDiscount > 0 && (
          <div className="flex justify-between text-green-700 font-medium mb-1 mt-2">
            <span>Hamper Discount</span>
            <span>-${hamperDiscount.toFixed(2)}</span>
          </div>
        )}

        {promoDiscount > 0 && (
          <div className="flex justify-between text-green-700 font-medium mb-1 mt-2">
            <span>
              Promo Discount ({promoCode.code}
              {promoCode.isPercent ? ` - ${promoCode.discountAmount}%` : ""}
              )
            </span>
            <span>-${promoDiscount.toFixed(2)}</span>
          </div>
        )}

        <div className="border-t my-4"></div>

        <div className="flex justify-between font-medium mb-1 mt-2">
          <span>Tax</span>
          <span>${taxAmount.toFixed(2)}</span>
        </div>

        <div className="flex justify-between text-lg font-bold">
          <span>Total Paid</span>
          <span>${totalAmount.toFixed(2)}</span>
        </div>
      </div>

      <div className="mt-8 flex gap-4 print:hidden">
        <button
          onClick={handleDownload}
          className="bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700"
        >
          Download / Print
        </button>
        <button
          onClick={() => navigate("/")}
          className="bg-gray-400 text-white px-4 py-2 rounded hover:bg-gray-500"
        >
          Back to Home
        </button>
      </div>
    </div>
  );
};

export default OrderConfirmation;
